// features/automationScripts/utils/placeholderCast.js
//
// Placeholder type casts — the ":type" suffix the execution engine reads to
// decide the JSON type of a resolved variable.
//
// A body field can only hold a placeholder in a *string* position:
//
//   { "id": "{{request_data.id}}" }
//
// so the engine used to guess the wire type from the shape of the resolved
// value (digits → number). A user who picked STRING for an id of "12345" got
// a number on the wire. The cast removes the guess:
//
//   { "id": "{{request_data.id:string}}" }   → always a JSON string
//   { "id": "{{request_data.id:number}}" }   → always a JSON number
//
// Rules mirrored from the engine contract:
//   • A cast only means something on a *whole-value* placeholder — one that is
//     the entire field value. Embedded in a larger string the result is a
//     string by construction, so we never emit a cast there.
//   • No cast = exactly the previous behaviour (inference). We therefore only
//     write a cast for fields the user has actually touched; a body loaded
//     from S3 round-trips byte-identical.
//   • Only the four keywords are recognised. Anything else after a ":" is part
//     of the variable name and is left completely alone.

// The only casts the engine recognises. `:int`, `:decimal` etc. are NOT
// recognised — they would be read as part of the variable name.
export const CAST_TYPES = ['string', 'number', 'boolean', 'json'];

// Field type (as shown in the type selector) → cast keyword.
const TYPE_TO_CAST = {
  string: 'string',
  number: 'number',
  boolean: 'boolean',
  object: 'json',
  array: 'json',
};

// Cast keyword → the field type the selector should pre-select.
const CAST_TO_TYPE = {
  string: 'string',
  number: 'number',
  boolean: 'boolean',
  json: 'object',
};

// One placeholder and nothing else — "{{request_data.id}}", not "id: {{x}}".
const WHOLE_PLACEHOLDER_RE = /^\{\{([^{}]*)\}\}$/;

// Trailing ":<cast>" inside a placeholder. Case-insensitive, whitespace around
// the colon allowed. Non-greedy head so the LAST suffix wins on "a:string:number".
const CAST_SUFFIX_RE = /^(.*?)\s*:\s*(string|number|boolean|json)\s*$/i;

/** The cast keyword for a field type, or null for an unknown type. */
export const castForFieldType = (type) => TYPE_TO_CAST[type] || null;

/** The field type a cast keyword should map back to in the type selector. */
export const fieldTypeForCast = (cast) => (cast ? CAST_TO_TYPE[String(cast).toLowerCase()] || null : null);

/**
 * Split a placeholder's inner text into its reference and its cast.
 * "request_data.id:string" → { ref: "request_data.id", cast: "string" }
 * "request_data.id"        → { ref: "request_data.id", cast: null }
 * "weird.name:int"         → { ref: "weird.name:int", cast: null }  (not a keyword)
 */
export const splitRefCast = (inner) => {
  if (!inner || typeof inner !== 'string') return { ref: inner, cast: null };
  const match = inner.match(CAST_SUFFIX_RE);
  if (!match) return { ref: inner, cast: null };
  return { ref: match[1], cast: match[2].toLowerCase() };
};

/** The reference with any cast suffix removed. */
export const stripRefCast = (inner) => splitRefCast(inner).ref;

/** True when the whole value is a single placeholder — the only castable shape. */
export const isWholeValuePlaceholder = (value) =>
  typeof value === 'string' && WHOLE_PLACEHOLDER_RE.test(value);

/**
 * The cast written into a whole-value placeholder, or null.
 * Returns null for embedded placeholders and plain values.
 */
export const readValueCast = (value) => {
  if (!isWholeValuePlaceholder(value)) return null;
  return splitRefCast(value.match(WHOLE_PLACEHOLDER_RE)[1]).cast;
};

/** A whole-value placeholder with any cast suffix removed. Other values pass through. */
export const stripValueCast = (value) => {
  if (!isWholeValuePlaceholder(value)) return value;
  const inner = value.match(WHOLE_PLACEHOLDER_RE)[1];
  const { ref, cast } = splitRefCast(inner);
  return cast ? `{{${ref}}}` : value;
};

/**
 * Write `cast` onto a whole-value placeholder, replacing any cast already there.
 * Anything that is not a whole-value placeholder — and a null cast — is
 * returned unchanged, so this is safe to call on every field.
 */
export const applyValueCast = (value, cast) => {
  if (!cast || !isWholeValuePlaceholder(value)) return value;
  const normalized = String(cast).toLowerCase();
  if (!CAST_TYPES.includes(normalized)) return value;
  const { ref } = splitRefCast(value.match(WHOLE_PLACEHOLDER_RE)[1]);
  return `{{${ref}:${normalized}}}`;
};
